import { Component, Inject, Input, LOCALE_ID, OnInit } from '@angular/core';
import { take } from 'rxjs';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { CurrencyModel, CurrencyService, HistoryService } from '@agenda-customer-ui/data-access';
import { NgIf, NgFor, UpperCasePipe, CurrencyPipe } from '@angular/common';
import { SharedModuleLib } from '../../../../../shared.module';
import { NoContentComponent } from '../no-content/no-content.component';
import { CardHistoryComponent } from '../../../card/card.component';
import { COUNTRY_CODE, HTTP_CODE } from '@core/enums';
import { getDayOfWeek, parseDateWithWeek } from '@core/utils';
import { HourPipe } from '../../../pipes/hour/hour.pipe';
import { RevisionPart, ScheduleDetails, ScheduleHistory, Service } from '../../../../../../../../core/src/interfaces/from-history/models';
import { AccordionLibComponent, TableComponent, TableElement } from '@components/atoms';

interface Accordion {
  open: boolean;
  scheduleDate: string;
  scheduleHour: number;
}

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'app-schedule',
    templateUrl: './schedule.component.html',
    styleUrls: ['./schedule.component.scss'],
    providers: [CurrencyService],
    standalone: true,
    imports: [
        NgIf,
        SharedModuleLib,
        NoContentComponent,
        NgFor,
        AccordionLibComponent,
        CardHistoryComponent,
        TableComponent,
        UpperCasePipe,
        CurrencyPipe,
        TranslateModule,
        HourPipe,
    ],
})
export class ScheduleComponent implements OnInit {
  @Input() vin!: string;
  @Input() country!: string;

  history: ScheduleHistory[] = [];

  vehicleImage!: string;

  scheduleDetails!: ScheduleDetails;

  lastOpenList: Accordion[] = [];

  schedulesLoaded = false;

  revision!: Service;

  services: Service[] = [];

  currency: CurrencyModel = new CurrencyModel();

  constructor(
    private historyService: HistoryService,
    private currencyService: CurrencyService,
    private translate: TranslateService,
    @Inject(LOCALE_ID) private locale: string
  ) {}

  ngOnInit(): void {
    this.currency = this.currencyService.getCurrencyCode(this.country);
    this.getScheduleHistory();
  }

  getRevisionPartsElements(revisionParts: RevisionPart[]): TableElement[] {
    return (revisionParts || [])
      .filter((part) => part.longPartDesc)
      .map((part, index) => ({
        id: index,
        name: part.longPartDesc,
        description: '',
      }));
  }

  getServicesElements(services: Service[]): TableElement[] {
    const currencyPipe = new CurrencyPipe(this.locale);
    return (services || []).map((service, index) => {
      const name = service.serviceName.includes('km')
        ? this.translate.instant('schedule.pickup-delivery')
        : service.serviceName;

      let description = this.translate.instant('budget.on-request');
      if (service.subtotal !== null && service.subtotal > 0 && service.promotionalPrice === null) {
        description = currencyPipe.transform(service.subtotal, this.currency.currency) || '';
      } else if (service.promotionalPrice !== null && service.promotionalPrice >= 0) {
        description = currencyPipe.transform(service.promotionalPrice, this.currency.currency) || '';
      }

      return { id: index, name, description };
    });
  }

  getScheduleHistory() {
    this.historyService
      .getScheduleHistory(this.vin, this.country)
      .pipe(take(1))
      .subscribe({
        next: (history) => {
          if (history && history.httpCode === HTTP_CODE.OK) {
            this.history = history.response;
            this.addDateToList();
            this.history.forEach(
              (history) =>
                (history.scheduleDateToDateType = new Date(history.scheduleDate))
            );
            // Takes the date and transforms into type DATE for sorting
            this.history.sort(
              (a, b) =>
                b.scheduleDateToDateType.getTime() -
                a.scheduleDateToDateType.getTime()
            );
          }
          this.schedulesLoaded = true;
        },
        error: () => {
          this.schedulesLoaded = true;
        }
      });
  }

  addDateToList() {
    this.history.forEach((history) => {
      history.parsedDate = this.parseDate(history.scheduleDate);
      history.extendedDate = this.extendDate(history.scheduleDate);
    });
  }

  parseDate(date: string) {
    const newDate = new Date(date + 'T00:00:00');
    const dayOfWeek = getDayOfWeek(
      newDate.getDay(),
      this.country || COUNTRY_CODE.BRAZIL
    );
    return parseDateWithWeek(newDate, dayOfWeek);
  }

  extendDate(date: string) {
    const newDate = new Date(date + 'T00:00:00');
    const month: string = this.translate.instant('months.' + newDate.getMonth()).toLowerCase();
    const extendedDate = (newDate.getDate()) + ' de ' + month  + ' de ' + newDate.getFullYear();
    return extendedDate;
  }

  getQuotationFromSchedule(schedule: ScheduleHistory) {
    this.historyService
      .getScheduleQuotation(
        schedule.advisorId,
        schedule.scheduleDate,
        schedule.scheduleHour,
        schedule.dealerId,
        schedule.country
      )
      .subscribe({
        next: (quotation) => {
          if (quotation) {
            this.scheduleDetails = quotation.response;
            this.getRevisions(this.scheduleDetails);
            this.services = this.scheduleDetails.requestedItems.services.filter(
              (service) => {
                return service.serviceType !== 'R';
              }
            );
            this.scheduleDetails.totalPrice =
              this.scheduleDetails.complementaryBudget.subtotal +
              this.scheduleDetails.recommendedItems.subtotal +
              this.scheduleDetails.requestedItems.subtotal;
            schedule.scheduleDetails = this.scheduleDetails;
            this.trimServiceName(this.scheduleDetails);
          } else {
            schedule.scheduleDetails = Object.create({});
          }
        },
        error: () => {
          schedule.scheduleDetails = Object.create({});
        }
      });
  }

  trimServiceName(scheduleDetails: ScheduleDetails) {
    const services = scheduleDetails.requestedItems.services;
    for (const element of services) {
      element.serviceName = (element.serviceName.includes("km")) ? element.serviceName : element.serviceName.split('-')[0];
    }
  }

  getScheduleDetails(schedule: ScheduleHistory) {
    const accordionOpen = this.lastOpenList.filter(
      (h) =>
        h.scheduleDate === schedule.scheduleDate &&
        h.scheduleHour === schedule.scheduleHour
    );
    if (accordionOpen.length === 0) {
      // pushes new data to array for not duplicating the request
      this.lastOpenList.push({
        open: true,
        scheduleDate: schedule.scheduleDate,
        scheduleHour: schedule.scheduleHour,
      });
      this.getQuotationFromSchedule(schedule);
    }
  }

  getRevisions(schedule: ScheduleDetails) {
    this.revision = schedule.requestedItems.services.filter(
      (service) => {
        return service.serviceType === 'R';
      }
    )[0];
  }
}
